import { useState } from 'react';
import axios from 'axios';
import { Check, Loader2, Upload, X, ArrowRight, ArrowLeft } from 'lucide-react';
import { SERVICES_REQUIRED_OPTIONS, PROPERTY_TYPES, BEDROOMS } from '../data/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  full_name: '', phone: '', email: '',
  collection_address: '', delivery_address: '',
  move_date: '', preferred_time: '',
  property_type: '', bedrooms: '',
  lift_available: '', stairs: '', parking_available: '',
  services_required: [],
  inventory_notes: '',
  photos: [],
  marketing_consent: false,
};

export default function QuoteForm({ sourcePage = 'website', compact = false }) {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const update = (k, v) => setData(d => ({ ...d, [k]: v }));

  const toggleService = (s) => {
    setData(d => ({
      ...d,
      services_required: d.services_required.includes(s)
        ? d.services_required.filter(x => x !== s)
        : [...d.services_required, s],
    }));
  };

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6);
    const readers = files.map(f => new Promise(res => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.readAsDataURL(f);
    }));
    const photos = await Promise.all(readers);
    setData(d => ({ ...d, photos: [...d.photos, ...photos].slice(0, 6) }));
  };

  const removePhoto = (i) => setData(d => ({ ...d, photos: d.photos.filter((_, idx) => idx !== i) }));

  const validStep1 = data.collection_address && data.delivery_address;
  const validStep2 = data.property_type && data.bedrooms;
  const validStep3 = data.full_name && data.phone && data.email;

  const submit = async () => {
    setSubmitting(true); setError('');
    try {
      await axios.post(`${API}/leads`, { ...data, source_page: sourcePage });
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Something went wrong. Please call us instead.');
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div data-testid="quote-form-success" className="bg-white rounded-2xl border border-sand p-8 md:p-12 text-center">
        <div className="w-16 h-16 rounded-full bg-forest grid place-items-center mx-auto mb-5">
          <Check size={28} className="text-white" />
        </div>
        <h3 className="font-outfit text-2xl md:text-3xl font-semibold text-ink mb-3">Thank you!</h3>
        <p className="font-manrope text-base text-ink/70 max-w-md mx-auto">
          We've received your quote request. A member of our team will be in touch shortly — we typically reply within 10 minutes during working hours.
        </p>
        <p className="font-manrope text-sm text-ink/50 mt-4">Need to speak to us urgently? Call <a href="tel:+442038577761" className="underline text-forest">020 3857 7761</a>.</p>
      </div>
    );
  }

  return (
    <div data-testid="quote-form" className="bg-white rounded-2xl border border-sand p-6 md:p-10">
      {/* Progress */}
      <div className="flex items-center gap-2 mb-7">
        {[1, 2, 3].map(s => (
          <div key={s} className="flex-1 flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full grid place-items-center text-xs font-semibold ${step >= s ? 'bg-forest text-white' : 'bg-sand text-ink/60'}`}>{s}</div>
            {s < 3 && <div className={`flex-1 h-0.5 ${step > s ? 'bg-forest' : 'bg-sand'}`} />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-5">
          <div>
            <h3 className="font-outfit text-xl md:text-2xl font-semibold text-ink">Where are you moving?</h3>
            <p className="font-manrope text-sm text-ink/60 mt-1">Tell us the collection and delivery addresses.</p>
          </div>
          <Field label="Collection address" testid="qf-collection">
            <input type="text" value={data.collection_address} onChange={e => update('collection_address', e.target.value)} placeholder="e.g. 12 High Street, Greenwich, SE10" className={inputCls} data-testid="qf-collection-input" />
          </Field>
          <Field label="Delivery address" testid="qf-delivery">
            <input type="text" value={data.delivery_address} onChange={e => update('delivery_address', e.target.value)} placeholder="e.g. 4 Mill Lane, Bromley, BR1" className={inputCls} data-testid="qf-delivery-input" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Move date"><input type="date" value={data.move_date} onChange={e => update('move_date', e.target.value)} className={inputCls} data-testid="qf-date-input" /></Field>
            <Field label="Preferred time">
              <select value={data.preferred_time} onChange={e => update('preferred_time', e.target.value)} className={inputCls} data-testid="qf-time-input">
                <option value="">Any time</option>
                <option>Morning (7am–12pm)</option>
                <option>Afternoon (12pm–5pm)</option>
                <option>Evening (5pm–9pm)</option>
                <option>Flexible</option>
              </select>
            </Field>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <div>
            <h3 className="font-outfit text-xl md:text-2xl font-semibold text-ink">Property details</h3>
            <p className="font-manrope text-sm text-ink/60 mt-1">Helps us quote the right team and van size.</p>
          </div>
          <Field label="Property type">
            <div className="grid grid-cols-4 gap-2">
              {PROPERTY_TYPES.map(p => (
                <button type="button" key={p} onClick={() => update('property_type', p)} data-testid={`qf-prop-${p.toLowerCase().replace(' ', '-')}`}
                  className={`py-3 px-2 rounded-xl border text-sm font-medium transition-all ${data.property_type === p ? 'bg-forest text-white border-forest' : 'bg-white border-sand text-ink hover:border-forest'}`}>{p}</button>
              ))}
            </div>
          </Field>
          <Field label="Size">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {BEDROOMS.map(b => (
                <button type="button" key={b} onClick={() => update('bedrooms', b)}
                  data-testid={`qf-bed-${b.toLowerCase().replace('+', 'plus').replace(' ', '-')}`}
                  className={`py-3 px-2 rounded-xl border text-sm font-medium transition-all ${data.bedrooms === b ? 'bg-forest text-white border-forest' : 'bg-white border-sand text-ink hover:border-forest'}`}>{b}</button>
              ))}
            </div>
          </Field>
          <div className="grid grid-cols-3 gap-4">
            <YesNo label="Lift available?" value={data.lift_available} onChange={v => update('lift_available', v)} />
            <YesNo label="Stairs?" value={data.stairs} onChange={v => update('stairs', v)} />
            <YesNo label="Parking?" value={data.parking_available} onChange={v => update('parking_available', v)} />
          </div>
          <Field label="Services required">
            <div className="flex flex-wrap gap-2">
              {SERVICES_REQUIRED_OPTIONS.map(s => (
                <button type="button" key={s} onClick={() => toggleService(s)}
                  className={`px-4 py-2 rounded-full border text-sm font-medium transition-all ${data.services_required.includes(s) ? 'bg-oat text-ink border-oat' : 'bg-white border-sand text-ink hover:border-oat'}`}>{s}</button>
              ))}
            </div>
          </Field>
          <Field label="Inventory notes (optional)">
            <textarea value={data.inventory_notes} onChange={e => update('inventory_notes', e.target.value)} rows={3} placeholder="e.g. 3-seat sofa, double bed, washing machine, 20 boxes…" className={inputCls + ' resize-none'} />
          </Field>
          <Field label="Photos (optional, up to 6)">
            <label className="flex items-center gap-2 px-4 py-3 border border-dashed border-sand rounded-xl cursor-pointer hover:border-forest text-sm text-ink/70">
              <Upload size={16} /> <span>Upload photos of items / rooms</span>
              <input type="file" accept="image/*" multiple onChange={handlePhotos} className="hidden" data-testid="qf-photo-input" />
            </label>
            {data.photos.length > 0 && (
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-3">
                {data.photos.map((p, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-sand">
                    <img src={p} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white grid place-items-center"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </Field>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-5">
          <div>
            <h3 className="font-outfit text-xl md:text-2xl font-semibold text-ink">Your contact details</h3>
            <p className="font-manrope text-sm text-ink/60 mt-1">We typically reply within 10 minutes during working hours.</p>
          </div>
          <Field label="Full name"><input type="text" value={data.full_name} onChange={e => update('full_name', e.target.value)} className={inputCls} data-testid="qf-name-input" /></Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Phone number"><input type="tel" value={data.phone} onChange={e => update('phone', e.target.value)} className={inputCls} data-testid="qf-phone-input" /></Field>
            <Field label="Email address"><input type="email" value={data.email} onChange={e => update('email', e.target.value)} className={inputCls} data-testid="qf-email-input" /></Field>
          </div>
          <label className="flex items-start gap-3 p-4 rounded-xl border border-sand bg-sand/30 cursor-pointer hover:border-forest transition-colors" data-testid="qf-marketing-consent-wrapper">
            <input type="checkbox" checked={data.marketing_consent} onChange={e => update('marketing_consent', e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-forest shrink-0" data-testid="qf-marketing-consent" />
            <span className="font-manrope text-sm text-ink/75 leading-relaxed">
              I would like to receive moving tips, service updates and special offers from Laraé Logistics. I understand I can unsubscribe at any time.
            </span>
          </label>
          {error && <p data-testid="qf-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2.5 rounded-lg">{error}</p>}
          <p className="font-manrope text-xs text-ink/50">By submitting you agree to be contacted about your enquiry. We don't share your data — see our Privacy Policy.</p>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 mt-8">
        {step > 1 ? (
          <button type="button" onClick={() => setStep(step - 1)} className="flex items-center gap-2 px-5 py-3 rounded-full border border-sand text-ink/70 hover:bg-sand text-sm font-medium" data-testid="qf-back-btn">
            <ArrowLeft size={16} /> Back
          </button>
        ) : <div />}
        {step < 3 ? (
          <button type="button" disabled={(step === 1 && !validStep1) || (step === 2 && !validStep2)} onClick={() => setStep(step + 1)}
            className="flex items-center gap-2 px-6 py-3 rounded-full bg-forest text-white text-sm font-medium hover:bg-forest/90 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="qf-next-btn">
            Continue <ArrowRight size={16} />
          </button>
        ) : (
          <button type="button" disabled={!validStep3 || submitting} onClick={submit}
            className="flex items-center gap-2 px-6 py-3 rounded-full bg-forest text-white text-sm font-semibold hover:bg-forest/90 disabled:opacity-50" data-testid="qf-submit-btn">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
            Get My Free Quote
          </button>
        )}
      </div>
    </div>
  );
}

const inputCls = 'w-full px-4 py-3 rounded-xl border border-sand bg-white text-ink placeholder:text-ink/40 focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20 transition-colors text-sm';

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="font-manrope text-sm font-medium text-ink mb-2 block">{label}</label>
      {children}
    </div>
  );
}

function YesNo({ label, value, onChange }) {
  return (
    <div>
      <label className="font-manrope text-xs font-medium text-ink/70 mb-2 block">{label}</label>
      <div className="grid grid-cols-2 gap-2">
        {['Yes', 'No'].map(v => (
          <button type="button" key={v} onClick={() => onChange(v)}
            className={`py-2 rounded-lg border text-sm font-medium transition-all ${value === v ? 'bg-forest text-white border-forest' : 'bg-white border-sand text-ink hover:border-forest'}`}>{v}</button>
        ))}
      </div>
    </div>
  );
}
